import { z } from 'zod';

/**
 * Shared primitives for the content, rights and world contracts. Plain data only: these schemas
 * are used by the API (row validation), domain rules, the browser and the world schema.
 */

export const LOCALES = ['en', 'et', 'fi', 'de'] as const;
export const localeSchema = z.enum(LOCALES);
export type Locale = z.infer<typeof localeSchema>;
/** Content fallback language (spec: Assumption 3). */
export const FALLBACK_LOCALE: Locale = 'en';

/** ISO 3166-1 alpha-2 format. Rights grants may name any country, not only pilot ones. */
export const countryCodeSchema = z
  .string()
  .regex(/^[A-Z]{2}$/, 'Expected an ISO 3166-1 alpha-2 code');
export type CountryCode = z.infer<typeof countryCodeSchema>;

/** Human-readable stable identifiers (slugs) used for editorial entities and seeds. */
export const entityIdSchema = z
  .string()
  .regex(/^[a-z0-9][a-z0-9-]{1,62}$/, 'Expected a lowercase slug identifier');

export const isoDateTimeSchema = z.iso.datetime({ offset: true });

function hasControlCharacters(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const c = value.charCodeAt(i);
    if ((c < 0x20 && c !== 0x09 && c !== 0x0a && c !== 0x0d) || c === 0x7f) return true;
  }
  return false;
}

/**
 * Plain editorial or user text: trimmed, length-limited and free of control characters.
 * Never rendered as HTML (React escapes it); no markup is interpreted anywhere.
 */
export function safeTextSchema(maxLength: number) {
  return z
    .string()
    .trim()
    .min(1)
    .max(maxLength)
    .refine((v) => !hasControlCharacters(v), 'Control characters are not allowed');
}

/** Per-locale text; English is mandatory as the content fallback. */
export function localizedTextSchema(maxLength: number) {
  return z
    .partialRecord(localeSchema, safeTextSchema(maxLength))
    .refine((v) => v.en !== undefined, 'English fallback text is required');
}
export type LocalizedText = Partial<Record<Locale, string>>;

/** Array schema that rejects duplicate entries. */
export function uniqueArraySchema<T extends z.ZodType>(item: T) {
  return z
    .array(item)
    .refine(
      (values) => new Set(values).size === values.length,
      'Duplicate entries are not allowed',
    );
}

export const ageSchema = z.int().min(3).max(18);

export const hexColourSchema = z.string().regex(/^#[0-9a-f]{6}$/, 'Expected #rrggbb (lowercase)');
